<template>
  <div class="d-flex" style="max-height: 100%;">

    <MarkerInfoModal
      :events="markerInfoModalEvents"
      :task="markerInfoModalTask"
      :showing="!!markerInfoModalTaskId"
      @close="hideMarkerInfoModal"
      @deviationApproved="$emit('deviationApproved')"
    />

    <!-- item list -->
    <div v-if="!displayOnly" class="tracker-list">

      <v-btn
        v-for="task in tasks"
        :key="task.id"
        class="tracker-list-item"
        @click="editTask(task.id)"
      >
        {{ task.name }}
      </v-btn>

      <!-- Static "add item" tracker -->
      <v-btn
        class="tracker-list-item-add text-decoration-none"
        @click="$router
        .push({ name: 'member-show-add-task', params: { userId: $route.params.userId } })"
      >
        <div class="d-flex flex-column">
          <v-icon color="#5D79B9" size="34">mdi-plus-circle-outline</v-icon>
          <div class="tracker-list-item-text">{{ $t('Add new task') }}</div>
        </div>
      </v-btn>

    </div>

    <!-- Calendar -->
    <div class="calendar-wrapper">
      <div ref="calendar" class="calendar" style="position: relative" v-dragscroll>
        <!-- Task names -->
        <div style="position: sticky; left: 0;height: 0;width:0;">
          <div
            v-for="(item, itemIndex) in tasks"
            :key="`${itemIndex}-item`"
            :style="`
            position: absolute;
            left: 4px;
            top: ${itemIndex * heightBasedOnWindow + 26 + 2}px; white-space: nowrap;`"
          >
            {{ item.name }}
          </div>
        </div>
        <!-- Times -->
        <div
          class="time-bar"
          :style="`
          margin-left: -${hourWidth/2}px;
          width: ${(timesToShow.length - 1) * hourWidth + (hourWidth / 2)}px;
          `"
        >
          <div
            v-for="(time, index) in timesToShow"
            :key="`time-${index}`"
            class="time-text"
            :style="`
            left: ${index * hourWidth}px;
            width: ${hourWidth}px;
            `"
          >
            {{ time }}
          </div>
        </div>
        <svg
          :width="24 * hourWidth"
          :height="boxHeight"
          :viewBox="`0  0 ${24 * hourWidth} ${boxHeight}`"
        >
          <!-- outer box -->
          <rect
            :height="heightBasedOnWindow * (tasks.length + 1)"
            :width="24 * hourWidth"
            style="stroke: #ddd"
          ></rect>

          <!-- Vertical rectangles -->
          <rect
            v-for="(task, index) in tasks"
            :key="`${index}-vert-outer`"
            :y="(index) * heightBasedOnWindow"
            :height="heightBasedOnWindow"
            :width="24 * hourWidth"
            style="fill: #fff;">
          </rect>
          <rect
            v-for="(task, index) in tasks"
            :key="`${index}-vert-inner`"
            :y="(index) * heightBasedOnWindow+ (heightBasedOnWindow / 4)"
            :height="heightBasedOnWindow / 2"
            :width="24 * hourWidth"
            style="fill: #f4f4f4"
          ></rect>

          <!-- Vertical rectangles (static for "add new") -->
          <rect
            v-if="!displayOnly"
            :y="(tasks.length) * heightBasedOnWindow"
            :height="heightBasedOnWindow"
            :width="24 * hourWidth"
            style="fill: #fff;"
          ></rect>
          <rect
            v-if="!displayOnly"
            :y="(tasks.length) *
            heightBasedOnWindow +
            (heightBasedOnWindow / 4)"
            :height="heightBasedOnWindow / 2"
            :width="24 * hourWidth"
            style="fill: #f4f4f4"
          ></rect>

          <!-- vertical lines -->
          <g
            v-for="(time, index) in timesToShow"
            :key="index"
          >
            <line
              :x1="index * hourWidth"
              :x2="index * hourWidth"
              :y1="0" :y2="lineHeight"
              stroke="#ccc">

            </line>
          </g>

          <!-- Render items -->
          <g v-for="(item, itemIndex) in tasks" :key="`${itemIndex}-item`">

            <!-- Render Zones -->
            <g
              v-for="(zone, zoneIndex) in item.zones"
              :key="`${zoneIndex}-zone`"
            >
              <!-- Zone -->
              <rect
                class="calendar-zone"
                :ref="`event-${zone.eventId}`"
                :class="{
                  'paused-effect': item.isPaused === true,
                  'highlight': highlightId === zone.eventId,
                  'calendar-zone-clickable': zone.type === 'missed' &&
                  zone.events.length === 1 &&
                  !zone.events[0].approvedBy
                }"
                :height="heightBasedOnWindow / 2 - (heightBasedOnWindow * 0.10)"
                :width="timesToLength(zone.startTime, zone.endTime)"
                :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2)"
                :x="getXFromTime(zone.startTime)"
                :fill="zone.backgroundFill || zone.fill"
                style="fill-opacity: 0.3"
                :rx="(heightBasedOnWindow / 2 - (heightBasedOnWindow * 0.10)) / 2"
                @click="zone.type === 'missed' && !zone.events[0].approvedBy ?
                showMarkerTooltip(item.id, zone) :
                null"
              >
              </rect>
              <!-- Zone timestamps -->
              <g class="calendar-zone-timestamps">
                <!-- Start timestamp -->
                <line
                  :x1="getXFromTime(zone.startTime)"
                  :x2="getXFromTime(zone.startTime)"
                  :y1="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 2)"
                  :y2="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 40"
                  :stroke="zone.fill"
                  stroke-opacity="0.5"
                ></line>
                <text
                  :x="getXFromTime(zone.startTime)"
                  :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 50"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  font-size="12"
                >{{ dateToHumanTime(zone.startTime) }}</text>

                <!-- End timestamp -->
                <line
                  :x1="getXFromTime(zone.endTime)"
                  :x2="getXFromTime(zone.endTime)"
                  :y1="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 2)"
                  :y2="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 40"
                  :stroke="zone.fill"
                  stroke-opacity="0.5"
                ></line>
                <text
                  :x="getXFromTime(zone.endTime)"
                  :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
                  ((heightBasedOnWindow * 0.10) / 2) + 50"
                  dominant-baseline="middle"
                  text-anchor="middle"
                  font-size="12"
                >{{ dateToHumanTime(zone.endTime) }}</text>
              </g>
            </g>

            <!-- Render overdue lines -->
            <rect
              v-for="(overdue, index) in item.overdues"
              :key="index"
              :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 4) +
              (heightBasedOnWindow / 8)"
              :x="getXFromTime(overdue.start)"
              :width="timesToLength(overdue.start, overdue.end)"
              :height="heightBasedOnWindow / 4"
              fill="#FF454B"
              :rx="heightBasedOnWindow / 8"
            ></rect>

            <!-- Render markers -->
            <g
              v-for="(marker, markerIndex) in item.markers"
              :key="`${markerIndex}-marker`"
            >

              <!-- Marker circle -->
              <circle
                v-if="marker.type !== 'extra' || !approvedDeviationsOnly"
                :ref="`event-${marker.events[0].id}`"
                class="calendar-marker"
                :class="trueHighlightId &&
                trueHighlightId === marker.events[0].id ? 'highlight' : ''"
                :r="trueHighlightId &&
                trueHighlightId === marker.events[0].id ?
                heightBasedOnWindow / 1.8 - (heightBasedOnWindow * 0.32) :
                heightBasedOnWindow / 2 - (heightBasedOnWindow * 0.32)"
                :fill="marker.fill"
                :cx="getXFromTime(marker.timestamp)"
                :cy="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 2)"
                @click="showMarkerTooltip(item.id, marker)"
                :style="marker.stroke ? `stroke: ${marker.stroke}; stroke-width: 4px;` : ''"
              >
              </circle>

              <!-- Marker inner icon -->
              <text
                v-if="marker.type !== 'extra' || !approvedDeviationsOnly"
                class="calendar-marker-icon"
                :x="getXFromTime(marker.timestamp)"
                :y="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 2) + 2"
                v-html="marker.icon ? marker.icon :
                marker.events.length > 1 ? marker.events.length : ''"
                fill="#fff"
                dominant-baseline="middle"
                text-anchor="middle"
              ></text>

              <!-- Note indication circle -->
              <circle
                v-if="marker.hasNotes"
                :class="trueHighlightId &&
                trueHighlightId === marker.events[0].id ? 'highlight' : ''"
                class="calendar-marker"
                :r="8"
                fill="#6484ff"
                :cx="getXFromTime(marker.timestamp) + 12"
                :cy="(itemIndex * heightBasedOnWindow) + (heightBasedOnWindow / 3)"
              >
              </circle>

              <!-- Note indication icon -->
              <svg v-if="marker.hasNotes"
                   :x="getXFromTime(marker.timestamp) + 8"
                   :y="(itemIndex * heightBasedOnWindow) + ((heightBasedOnWindow / 100) * 28)"
                   height="9px"
                   width="9px"
                   viewBox="0 0 24 24">
                <path
                  fill="#fff"
                  d="M9,22A1,1 0 0,1 8,21V18H4A2,2 0 0,1 2,16V4C2,2.89 2.9,2 4,2H20A2,
            2 0 0,1 22,4V16A2,2 0 0,1 20,18H13.9L10.2,21.71C10,
            21.9 9.75,22 9.5,22V22H9Z" />
              </svg>
            </g>
          </g>

          <!-- Render current time line -->
          <text
            v-if="!displayOnly"
            ref="nowMarker"
            :x="getXFromTime(currentTime)"
            y="10"
            text-anchor="middle"
            font-size="12px"
            color="#6484ff"
          >{{ $t('Now') }}</text>
          <line
            v-if="!displayOnly"
            :x1="getXFromTime(currentTime)"
            :x2="getXFromTime(currentTime)"
            :y1="0"
            :y2="(heightBasedOnWindow * tasks.length) + heightBasedOnWindow"
            stroke="#6484ff"
            stroke-opacity="0.7"
          ></line>

        </svg>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {
  addSeconds,
  differenceInSeconds,
  differenceInCalendarDays,
  format,
  isSameDay,
  isWithinInterval,
  isBefore,
  startOfDay,
  subSeconds, formatISO,
  fromUnixTime,
} from 'date-fns';
import MarkerInfoModal from './MarkerInfoModal.vue';
import {task} from "@vue/cli-plugin-eslint/ui/taskDescriptor";

export default {
  components: {
    MarkerInfoModal
  },
  props: {
    tasks: {
      type: Array,
      required: true,
      default: () => [],
    },
    displayOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    scaleDown: {
      type: Boolean,
      required: false,
      default: false
    },
    scaleUp: {
      type: Boolean,
      required: false,
      default: false
    },
    height: {
      type: Number,
      required: false,
      default: 100,
    }
  },
  data() {
    return {
      currentTime: Date.now(),
      currentTimeInterval: null,
      approvedDeviationsOnly: false,
      highlightId: null,
      trueHighlightId: null,
      markerTooltip: {
        show: false,
        text: [],
      },
      //isMounted is used to check if component is mounted, before looking for $refs in computed
      isMounted: false,
      hourWidth: 100,
      windowWidth: window.innerWidth,
      times: [
        '',
        '01:00',
        '02:00',
        '03:00',
        '04:00',
        '05:00',
        '06:00',
        '07:00',
        '08:00',
        '09:00',
        '10:00',
        '11:00',
        '12:00',
        '13:00',
        '14:00',
        '15:00',
        '16:00',
        '17:00',
        '18:00',
        '19:00',
        '20:00',
        '21:00',
        '22:00',
        '23:00',
        '',
      ],
      markerInfoModalTaskId: null,
      markerInfoModalEventIds: [],
    };
  },
  computed: {
    task() {
      return task
    },
    heightBasedOnWindow() {
      if (this.height === 100) {
        if (this.windowWidth < 1600) {
          return 80;
        }
        return this.height;
      } else {
        return this.height;
      }
    },
    timesToShow() {
      if (this.hourWidth < 46) {
        return this.times.map((item) => {
          return item.split(':')[0];
        })
      }
      return this.times;
    },
    lineHeight() {
      return (this.tasks.length + (this.displayOnly ? 0 : 1)) * this.heightBasedOnWindow;
    },
    boxHeight() {
      return (this.tasks.length + (this.displayOnly ? 0 : 1)) * this.heightBasedOnWindow;
    },
    markerInfoModalTask() {
      if (this.markerInfoModalTaskId) {
        return this.tasks.find((task) => task.id === this.markerInfoModalTaskId);
      }
      return null;
    },
    markerInfoModalEvents() {
      if (this.markerInfoModalTaskId) {
        const task = this.tasks.find(item => item.id === this.markerInfoModalTaskId);
        return task.events.filter((event) => this.markerInfoModalEventIds.includes(event.id));
      } else {
        return [];
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.resizeListener);
    this.isMounted = true;
    this.$nextTick(() => {
      this.hourWidth = this.getHourWidth();
    });

    // update current time every 30 seconds
    this.currentTimeInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 30000);
    if(!this.displayOnly) {
      this.$refs.nowMarker.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    }
  },
  beforeDestroy() {
    clearInterval(this.currentTimeInterval);
    window.removeEventListener('resize', this.resizeListener, false);
  },
  watch: {
    scaleUp() {
      this.hourWidth = this.getHourWidth();
    },
    scaleDown() {
      this.hourWidth = this.getHourWidth();
    }
  },
  methods: {
    isBefore,
    resizeListener() {
      this.windowWidth = window.innerWidth;
      this.hourWidth = this.getHourWidth();
    },
    getHourWidth() {
      const defaultWidth = 100;

      if (this.isMounted) {
        // This condition is currently never reached after first run
        if (this.scaleUp && this.$refs.calendar?.clientWidth > defaultWidth * 24) {
          return this.$refs.calendar.clientWidth / 24;
        }

        if(this.scaleDown && this.$refs.calendar?.clientWidth < defaultWidth * 24) {
          return this.$refs.calendar.clientWidth / 24;
        }
      }
      return defaultWidth;
    },
    dateToHumanTime(date) {
      return format(date, 'HH:mm')
    },
      getOverdueX(time) {
        if(!isSameDay(time, this.currentTime)) {
          return this.calenderXOffset;
        } else {
          return this.getXFromTime(time);
        }
      },
      getOverdueWidth(start, end) {
        if(!isSameDay(start, end)) {
          return this.timesToLength(startOfDay(end), end);
        } else {
          return this.timesToLength(start, end)
        }
      },
    timesToLength(start, end) {
      const seconds = Math.abs(differenceInSeconds(start, end));
      const secondsInADay = 86400;
      const totalPixels = 24 * this.hourWidth;
      // seconds required to generate 1 pixel (36 if totalPixels is 2400)
      const onePixel = secondsInADay / totalPixels;
      return Math.floor(seconds / onePixel);
    },
    getXFromTime(start) {
      let timeShouldAdjust = false;
      // Adjustments for daylight savings
      try {
        let dayStart = startOfDay(start);
        const startOfDayOffset = dayStart.getTimezoneOffset();
        let endOffset;
        if (typeof start === 'number') {
          endOffset = fromUnixTime(start / 1000).getTimezoneOffset();
        } else {
          endOffset = start.getTimezoneOffset();
        }
        // if time has skipped forward
        if (startOfDayOffset > endOffset) {
          timeShouldAdjust = 'forwards';
        } else if (startOfDayOffset < endOffset) {
          timeShouldAdjust = 'backwards';
        }
      } catch(err) {
      }
      let seconds = Math.abs(differenceInSeconds(startOfDay(start), start));
      const secondsInAnHour = 3600;
      if (timeShouldAdjust === 'forwards') {
        seconds += secondsInAnHour;
      } else if (timeShouldAdjust === 'backwards') {
        seconds -= secondsInAnHour;
      }
      const secondsInADay = 86400;
      const totalPixels = 24 * this.hourWidth;
      // seconds required to generate 1 pixel (36 if totalPixels is 2400)
      const onePixel = secondsInADay / totalPixels;
      if (differenceInCalendarDays(start, this.currentTime) === -1) {
        seconds -= secondsInADay;
      }
      return Math.floor(seconds / onePixel);
    },
    showMarkerTooltip(itemId, marker) {
      this.markerInfoModalTaskId = itemId;
      this.markerInfoModalEventIds = marker.events.map(event => event.id);
    },
    hideMarkerInfoModal() {
      this.markerInfoModalTaskId = null;
    },
    editTask(id) {
      this.$router.push({ name: 'member-show-edit-task', params: { taskId: id } })
    },
    goToDeviation(id) {
      this.highlightId = id;
      if (id) {
        let firstEventId;
        this.tasks.forEach(task => {
          if (firstEventId) return;
          task.markers.forEach(marker => {
            if (firstEventId) return;
            if (marker.events.find(event => event.id === id)) {
              firstEventId = marker.events[0].id;
            }
          })

          if (!firstEventId) {
            task.zones.forEach(zone => {
              if (firstEventId) return;
              if (zone.events.find(event => event.id === id)) {
                firstEventId = zone.events[0].id;
              }
            })
          }
        })
        if (firstEventId) {
          this.trueHighlightId = firstEventId;
          this.$refs[`event-${firstEventId}`][0].scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
        }
      } else {
        this.trueHighlightId = null;
      }
    },
  },
};

/* eslint-enable */

</script>

<style scoped lang="scss">
  .paused-effect {
    opacity: 0.3;
  }

  .tracker-list {
    display: flex;
    flex-direction: column;
    width: 120px;
    margin-top: 26px;
    max-height: 100%;
    overflow: auto;
  }

  .tracker-separator {
    height: 1px;
    border: none;
    background-color: $lightGrey;
    margin: 16px 0;
  }

  .tracker-list-item, .tracker-list-item-add {
    min-height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
    margin: 4px;
    border: 1px solid #eee;
    border-radius: 0 4px 4px 0;
    padding: 0px 12px !important;
    white-space: normal;

    &:hover, &-add:hover {
      background-color: rgba(132, 100, 255, 0.1);
    }
  }

  .tracker-list-item-text {
    font-weight: 500;
    padding: 4px;
    color: $primaryDark;
    white-space: normal !important;
  }

  .calendar-wrapper {
    flex: 1;
    max-height: 100%;
  }

  .calendar {
    max-width: calc(100vw - 420px);
    overflow-x: auto;
    cursor: grab;
    max-height: 100%;
  }

  .calendar:active {
    cursor: grabbing;
  }

  .calendar-marker {
    stroke-width: 0;
    cursor: pointer;
  }

  .calendar-marker:hover {
    stroke: #3ab43a;
    animation: zone-pulse 1.5s infinite ease-in-out;
  }

  .calendar-zone-clickable {
    cursor: pointer;
    stroke-width: 0;
  }

  .calendar-zone-clickable:hover {
    stroke: rgba(255, 69, 75, 0.2) !important;
    animation: zone-pulse 1.5s infinite ease-in-out;
  }

  @keyframes zone-pulse {
    0% {
      stroke-width: 0;
      stroke-opacity: 1;
    }

    70% {
      stroke-width: 16px;
      stroke-opacity: 0;
    }

    100% {
      stroke-width: 0;
      stroke-opacity: 0;
    }
  }

  .calendar-marker-icon {
    pointer-events: none;
  }

  .calendar-zone:hover + .calendar-zone-timestamps {
    opacity: 1 !important;
  }

  .calendar-zone-timestamps {
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
  }

  .time-bar {
    position: sticky;
    top: 0px;
    text-align: center;
    border-bottom: 1px solid #eee;
    background-color: white;
    height: 26px;
  }

  .time-text {
    display: flex;
    justify-content: center;
    background-color: #fff;
    position: absolute;
  }

  @keyframes highlight {
    0% { opacity: 0.3; }
    50% { opacity: 1; }
    100% { opacity: 0.3; }
  }

  .highlight {
    animation: highlight 2s linear infinite;
    fill-opacity: 1 !important;
  }

</style>
